import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import SearchProduct from './SearchProduct/SearchProduct';
import AppliedFilters from '../AppliedFilters/AppliedFilters';
import StickyWrapper from '../Common/StickyWrapper/StickyWrapper';
import FiltersDesktop from '../Filters/FiltersDesktop/FiltersDesktop';
import FiltersMobile from '../Filters/FiltersMobile/FiltersMobile';
import Pager from '../Pager/Pager';
import ProductList from '../ProductList/ProductList';
import ProductsViewed from '../ProductsViewed/ProductsViewed';
import Sorting from '../Sorting/Sorting';
import InsertHtmlContent from 'components/Common/InsertHtmlContent/InsertHtmlContent';
import Trans from 'components/Common/Translation/Trans';
import useFilters from 'hooks/listing/useFilters';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import { getUri } from 'services/generic';
import { formatListingProducts } from 'services/gtm/inputDataFormatter';
import {
  pushFilterChangeEvent,
  pushSearchProductClickEvent,
  pushSearchProductImpressionEvent,
} from 'services/listing/gtm';
import {
  GTM_PAGE_SEARCH,
  GTM_SEARCH_WITH_RESULT,
} from 'settings/gtm/gtmPageView';
import { LARGE_LAPTOP_AND_DESKTOP } from 'settings/mediaQuery';
import { SearchListingWithtResultType } from 'types/ProductListing/SearchProductListing';
import {
  FilterType,
  FilterValueType,
  ProductType,
} from 'types/ProductListing/ProductListing';

import styles from './SearchProducts.module.scss';

type PropsType = {
  title: string;
  data: SearchListingWithtResultType;
  isDataValidating: boolean;
};

export default function SearchProducts({
  title,
  data,
  isDataValidating,
}: PropsType): JSX.Element {
  const { pathname, state, search, hash } = useLocation();
  const { t } = useTranslation('listingPage');
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ query: LARGE_LAPTOP_AND_DESKTOP });
  const productsWrapperRef = useRef<HTMLDivElement | null>(null);
  const {
    banner,
    filters,
    pager,
    products,
    productCount,
    searchKeyword,
    sorting,
  } = data;
  const { applied: appliedFilters, list: filtersList } = useFilters(filters);
  const displayPager = Object.keys(pager.pages).length > 1;
  const initialUrl = pager.pages[1].split('?')[0];

  useEffect(() => {
    if (products.length) pushSearchProductImpressionEvent(products.slice(0, 6));
  }, [products]);

  useEffect(() => {
    if (state?.scrollToRef === true) {
      setTimeout(() => {
        if (productsWrapperRef.current) {
          productsWrapperRef.current.scrollIntoView({ behavior: 'auto' });
          // eslint-disable-next-line
          const { scroll, scrollToRef, ...rest } = state;
          navigate(pathname + search, {
            replace: true,
            state: rest,
          });
        }
      }, 100);
    }
  }, [pathname, search]);

  function handleFilterClick(filter: FilterType, filterValue: FilterValueType) {
    pushFilterChangeEvent(filter.label, filterValue.label);
  }

  function removeScrollToProductState() {
    if (state) {
      // eslint-disable-next-line
      const { scrollToProduct, ...rest } = state;
      navigate(pathname + search, {
        replace: true,
        state: rest,
      });
    }
  }

  function renderProduct(product: ProductType): JSX.Element {
    const { id } = product;
    return (
      <SearchProduct
        key={product.id}
        product={product}
        onClick={() => {
          pushSearchProductClickEvent(product);
          navigate(`${pathname}${search}${hash}`, {
            replace: true,
            state: { ...state, scrollToProduct: id },
          });
        }}
        scrollInto={state?.scrollToProduct === id}
        onScroll={removeScrollToProductState}
      />
    );
  }

  return (
    <>
      <GtmPageView
        title={title}
        products={products}
        searchTerm={searchKeyword}
        productCount={productCount}
        isDataValidating={isDataValidating}
      />
      {banner && (
        <InsertHtmlContent htmlContent={banner} className={styles.banner} />
      )}
      <h1 className={styles.title}>
        {t('SEARCH_RESULTS_FOR', { text: searchKeyword })}
      </h1>
      {isDesktop && filtersList.length && (
        <StickyWrapper className={styles.stickyWrapper}>
          {isSticky => (
            <FiltersDesktop
              filters={filtersList}
              onFilterClick={handleFilterClick}
              isSticky={isSticky}
              className={styles.filters}
            />
          )}
        </StickyWrapper>
      )}
      {isDesktop && !!appliedFilters.length && (
        <AppliedFilters
          filters={appliedFilters}
          resetUrl={getUri(initialUrl)}
          className={styles.appliedFilters}
        />
      )}
      <div className={styles.actionBar}>
        <span className={styles.nbProducts}>
          <Trans
            i18nKey={'NB_PRODUCTS'}
            namespace={'listingPage'}
            values={{ count: productCount.toString() }}
          />
        </span>
        {sorting && <Sorting sorting={sorting.list} />}
        {!isDesktop && filtersList.length && (
          <FiltersMobile
            filters={filtersList}
            resetUrl={getUri(initialUrl)}
            productCount={productCount}
            onFilterClick={handleFilterClick}
          />
        )}
      </div>
      <div className={styles.productListWrapper}>
        <ProductList
          ref={productsWrapperRef}
          products={products}
          renderProduct={renderProduct}
        />
      </div>
      <ProductsViewed
        count={pager.currentProductCount}
        total={productCount}
        className={styles.pagerProgression}
      />
      {displayPager && (
        <Pager pager={pager} classes={{ container: styles.pagerContainer }} />
      )}
    </>
  );
}

type GtmPageViewProps = {
  title: string;
  products: ProductType[];
  searchTerm: string;
  productCount: number;
  isDataValidating: boolean;
};

function GtmPageView({
  title,
  products,
  searchTerm,
  productCount,
  isDataValidating,
}: GtmPageViewProps) {
  useGtmPageView(GTM_PAGE_SEARCH, title, {
    products: formatListingProducts(products),
    searchTerm: searchTerm,
    searchStatus: GTM_SEARCH_WITH_RESULT,
    searchResultNumber: productCount,
    isLoaded: !isDataValidating,
  });

  return null;
}
